// Copyright (c) 2022-2024. Sendanor <info@sendanor.fi>. All rights reserved.

import React from "react";
import {
    CART_DETAILS_CLASS_NAME
} from "../../../../core/constants/className";
import {
    T_CART_TOTAL_SUM,
    T_CART_TOTAL_SUM_WITHOUT_VAT,
    T_CART_TOTAL_VAT,
    T_COMMON_PRICE_EUR, T_DATE_TIME_FORMAT
} from "../../../../core/constants/translation";
import { map } from "../../../../../../io/hyperify/core/functions/map";
import { ShoppingCartItem } from "../../../../../../io/hyperify/core/store/types/cart/ShoppingCartItem";
import { ShoppingCartUtils } from "../../../../../../io/hyperify/core/store/utils/ShoppingCartUtils";
import { CurrencyUtils } from "../../../../../../io/hyperify/core/CurrencyUtils";
import { SendanorProductUtils } from "../../../../core/utils/SendanorProductUtils";
import { TrashIcon } from "../../../assets/icons";
import { ShoppingCart } from "../../../../../../io/hyperify/core/store/types/cart/ShoppingCart";
import { getProductTypeTranslationKey, getSelectPriceTypeTextForPrice } from "../../../../../../io/hyperify/core/store/constants/storeTranslation";
import { TranslationFunction } from "../../../../../../io/hyperify/core/types/TranslationFunction";
import "./CartDetails.scss";

export interface CartDetailsProps {
    readonly t: TranslationFunction;
    readonly className?: string;
    readonly cart : ShoppingCart;
    readonly onRemove ?: (item: ShoppingCartItem) => void;
}

export function CartDetails (props: CartDetailsProps) {
    const t: TranslationFunction = props?.t;
    const onRemove = props?.onRemove;
    const className = props?.className;
    const cart = props?.cart;
    const cartItems = cart?.items ?? [];
    const totalSum: number = ShoppingCartUtils.getTotalSum(cartItems);
    const totalSumWithoutVat: number = ShoppingCartUtils.getTotalSumWithoutVat(cartItems);
    const totalVat: number = ShoppingCartUtils.getTotalVat(cartItems);
    const dateTimeFormat = t(T_DATE_TIME_FORMAT);

    return (
        <div className={CART_DETAILS_CLASS_NAME + (className ? ` ${className}` : "")}>
            {map(
                cartItems,
                (item: ShoppingCartItem) => {

                    const translationParams = SendanorProductUtils.createTranslationParamsFromPrice(
                        item.price,
                        dateTimeFormat,
                        t,
                    );

                    function onDeleteItem () {
                        if(onRemove) {
                            onRemove(item);
                        }
                    }

                    return (
                        <div
                            key={`cart-item-${item.id}`}
                            className={CART_DETAILS_CLASS_NAME + "-item"}
                        >
                            <div className={CART_DETAILS_CLASS_NAME + "-item-field " + CART_DETAILS_CLASS_NAME + "-item-title"}>
                                {t(getProductTypeTranslationKey(item.product.type), translationParams)}<br />
                                {t(item.product.title, translationParams)} {item.product.summary ? t(item.product.summary, translationParams) : null}<br />
                                {t(getSelectPriceTypeTextForPrice(item.price), translationParams)}
                            </div>
                            <div className={CART_DETAILS_CLASS_NAME + "-item-field " + CART_DETAILS_CLASS_NAME + "-item-amount"}>
                                {onRemove ? (
                                    <button
                                        onClick={onDeleteItem}
                                        className={CART_DETAILS_CLASS_NAME + "-remove-button button"}
                                    ><TrashIcon /></button>
                                ) : null}
                                {item.amount}x
                            </div>
                            <div className={CART_DETAILS_CLASS_NAME + "-item-field " + CART_DETAILS_CLASS_NAME + "-item-price"}>{t(
                                T_COMMON_PRICE_EUR,
                                translationParams
                            )}</div>
                        </div>
                    );
                }
            )}
            <div className={CART_DETAILS_CLASS_NAME + "-item cart-totals"}>
                <div className={CART_DETAILS_CLASS_NAME + "-item-field " + CART_DETAILS_CLASS_NAME + "-item-title"}>
                    {t(T_CART_TOTAL_SUM)}<br />
                    {t(T_CART_TOTAL_SUM_WITHOUT_VAT)}<br />
                    {t(T_CART_TOTAL_VAT)}
                </div>
                <div className={CART_DETAILS_CLASS_NAME + "-item-field " + CART_DETAILS_CLASS_NAME + "-item-price"}>
                    {t(T_COMMON_PRICE_EUR, {AMOUNT: CurrencyUtils.stringifySum(totalSum)})}<br />
                    {t(T_COMMON_PRICE_EUR, {AMOUNT: CurrencyUtils.stringifySum(totalSumWithoutVat)})}<br />
                    {t(T_COMMON_PRICE_EUR, {AMOUNT: CurrencyUtils.stringifySum(totalVat)})}
                </div>
            </div>
        </div>
    );
}
